import React, {useEffect, useState} from 'react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {FaEllipsisV, FaPlus} from 'react-icons/fa';
import {finalizarJogoDoDia} from "../services/jogoDoDiaService";
import {useNavigate, useParams} from "react-router-dom";
import {alertaDeErro, alertaDeSucesso, confirmarAcao} from "../services/alertServices";

export const JogosDoDia = () => {
    const navigate = useNavigate();
    const {id} = useParams();
    const [jogadores, setJogadores] = useState([]);
    const [jogos, setJogos] = useState([]);
    const [diaFinalizado, setDiaFinalizado] = useState(false);
    const [loadingJogadores, setLoadingJogadores] = useState(true);
    const [loadingJogos, setLoadingJogos] = useState(true);
    const [diaDeJogo, setDiaDeJogo] = useState({});

    const [modalNovoJogoAberto, setModalNovoJogoAberto] = useState(false);
    const [criandoJogo, setCriandoJogo] = useState(false);

    const [novoJogo, setNovoJogo] = useState({
        idJogadorA1: '',
        idJogadorA2: '',
        idJogadorB1: '',
        idJogadorB2: ''
    });

    const [editandoResultadoId, setEditandoResultadoId] = useState(null);
    const [edicaoResultado, setEdicaoResultado] = useState({});
    const [salvandoResultadoId, setSalvandoResultadoId] = useState(null);
    const [dropdownAbertoId, setDropdownAbertoId] = useState(false);
    const [jogoEmEdicao, setJogoEmEdicao] = useState(null);

    const toggleDropdown = (id) => {
        setDropdownAbertoId((atual) => (atual === id ? null : id));
    };

    useEffect(() => {
        const carregarDados = async () => {
            await Promise.all([carregarJogadores(), carregarJogos(), carregarStatusDoDia()]);
        };
        carregarDados();
    }, [id]);

    const [finalizandoJogoDoDia, setFinalizandoJogoDoDia] = useState(false);

    const carregarJogadores = async () => {
        try {
            setLoadingJogadores(true);
            const response = await fetch('/api/Jogadores');
            if (!response.ok) {
                throw new Error('Erro ao carregar jogadores');
            }
            const data = await response.json();
            setJogadores(data);
        } catch (e) {
            console.error(e);
            await alertaDeErro('Erro ao carregar jogadores.');
        } finally {
            setLoadingJogadores(false);
        }
    };

    const carregarStatusDoDia = async () => {
        try {
            const url = id ? `/api/DiasDeJogo/${id}` : '/api/DiasDeJogo/Atual';
            const response = await fetch(url);
            if (response.ok && response.status === 200) {
                const data = await response.json();
                setDiaDeJogo(data);
                setDiaFinalizado(data.status === 1); // finalizado
            }
        } catch (e) {
            console.error("Erro ao carregar status do dia", e);
        }
    };

    const carregarJogos = async () => {
        try {
            setLoadingJogos(true);
            const url = id ? `/api/DiasDeJogo/${id}/Jogos` : '/api/Jogos/DiaAtual';
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Erro ao carregar jogos do dia');
            }
            const data = await response.json();
            setJogos(data);

            setEditandoResultadoId(null);
            setEdicaoResultado({});
        } catch (e) {
            console.error(e);
            await alertaDeErro('Erro ao carregar jogos do dia.');
        } finally {
            setLoadingJogos(false);
        }
    };

    const toggleModalNovoJogo = () => {
        setModalNovoJogoAberto(!modalNovoJogoAberto);
        if (!modalNovoJogoAberto) {
            setNovoJogo({
                idJogadorA1: '',
                idJogadorA2: '',
                idJogadorB1: '',
                idJogadorB2: ''
            });
        }
    };

    const handleChangeNovoJogo = (campo, valor) => {
        setNovoJogo(prev => ({
            ...prev,
            [campo]: valor ? parseInt(valor, 10) : ''
        }));
    };

    const criarJogo = async () => {
        const {idJogadorA1, idJogadorA2, idJogadorB1, idJogadorB2} = novoJogo;

        if (!idJogadorA1 || !idJogadorA2 || !idJogadorB1 || !idJogadorB2) {
            await alertaDeErro('Selecione todos os jogadores para criar o jogo.');
            return;
        }

        try {
            setCriandoJogo(true);
            const response = await fetch('/api/Jogos', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    id: jogoEmEdicao?.id || null,
                    idJogadorA1,
                    idJogadorA2,
                    idJogadorB1,
                    idJogadorB2
                })
            });

            if (!response.ok) {
                throw new Error('Erro ao criar jogo.');
            }

            await carregarJogos();
            toggleModalNovoJogo();
        } catch (e) {
            console.error(e);
            await alertaDeErro('Erro ao criar jogo.');
        } finally {
            setCriandoJogo(false);
        }
    };

    const renderSelectJogador = (valor, onChange, placeholder, excludedIds = []) => (
        <select
            className="form-select"
            value={valor ?? ''}
            onChange={e => onChange(e.target.value)}
        >
            <option value="">{placeholder}</option>
            {jogadores
                .filter(j => !excludedIds.includes(j.id))
                .map(j => (
                    <option key={j.id} value={j.id}>
                        {j.nome}
                    </option>
                ))}
        </select>
    );

    const nomeJogador = (id) => {
        const j = jogadores.find(x => x.id === id);
        return j ? j.nome : '-';
    };

    const iniciarEdicaoResultado = (jogo) => {
        setEditandoResultadoId(jogo.id);
        setEdicaoResultado(prev => ({
            ...prev,
            [jogo.id]: {
                gamesA: jogo.gamesA != null ? String(jogo.gamesA) : '',
                gamesB: jogo.gamesB != null ? String(jogo.gamesB) : ''
            }
        }));
    };

    const cancelarEdicaoResultado = (jogoId) => {
        setEditandoResultadoId(null);
        setEdicaoResultado(prev => {
            const copia = {...prev};
            delete copia[jogoId];
            return copia;
        });
    };

    const alterarCampoResultado = (jogoId, campo, valor) => {
        const somenteNumero = valor.replace(/\D/g, '');
        setEdicaoResultado(prev => ({
            ...prev,
            [jogoId]: {
                ...prev[jogoId],
                [campo]: somenteNumero
            }
        }));
    };

    const salvarResultado = async (jogoId) => {
        const edicao = edicaoResultado[jogoId] || {gamesA: '', gamesB: ''};
        const gamesA = edicao.gamesA === '' ? null : parseInt(edicao.gamesA, 10);
        const gamesB = edicao.gamesB === '' ? null : parseInt(edicao.gamesB, 10);

        if (gamesA === null || gamesB === null) {
            await alertaDeErro('Informe os games de A e B para salvar o resultado.');
            return;
        }

        try {
            setSalvandoResultadoId(jogoId);

            const response = await fetch(`/api/Jogos/${jogoId}/Finalizar`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    gamesA,
                    gamesB
                })
            });

            if (!response.ok) {
                const msg = await response.text();
                throw new Error(msg || 'Erro ao salvar resultado');
            }

            await carregarJogos();

        } catch (e) {
            console.error(e);
            await alertaDeErro(e.message || 'Erro ao salvar resultado.');
        } finally {
            setSalvandoResultadoId(null);
        }
    };

    const handleFinalizarJogoDoDia = async () => {
        setFinalizandoJogoDoDia(true);

        const idDiaAtual = id || diaDeJogo?.id;
        try {
            if (!idDiaAtual) {
                await alertaDeErro('Não foi possível identificar o dia de jogo atual');
                return;
            }

            const result = await confirmarAcao(
                'Tem certeza?',
                'Você deseja finalizar os jogos deste dia?',
                'Sim, finalizar!'
            );

            if (!result.isConfirmed) {
                return;
            }

            await finalizarJogoDoDia(idDiaAtual);

            await alertaDeSucesso('Dia de jogo finalizado com sucesso!');
            navigate('/');
        } catch (e) {
            console.error(e);
            await alertaDeErro(e.message || 'Erro ao finalizar dia de jogo.');
        } finally {
            setFinalizandoJogoDoDia(false);
        }
    };

    const handleAbrirModalParaEditarJogo = (jogo) => {
        setJogoEmEdicao(jogo);
        setNovoJogo({
            idJogadorA1: jogo.idJogadorA1,
            idJogadorA2: jogo.idJogadorA2,
            idJogadorB1: jogo.idJogadorB1,
            idJogadorB2: jogo.idJogadorB2
        });
        setModalNovoJogoAberto(true);
    };

    const handleDuplicarJogo = async (id) => {
        try {
            const response = await fetch(`/api/Jogos/${id}/Duplicar`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'}
            });

            if (!response.ok) {
                throw new Error('Erro ao duplicar jogo.');
            }

            await carregarJogos();
        } catch (e) {
            console.error(e);
            await alertaDeErro('Erro ao duplicar jogo.');
        }
    };

    const handleExcluirJogo = async (id) => {
        const result = await confirmarAcao(
            'Tem certeza?',
            'Você deseja excluir esse jogo?',
            'Sim, excluir!'
        );
        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await fetch(`/api/Jogos/${id}`, {
                method: 'DELETE',
                headers: {'Content-Type': 'application/json'}
            });

            if (!response.ok) {
                throw new Error('Erro ao excluir jogo.');
            }

            await carregarJogos();
        } catch (e) {
            console.error(e);
            await alertaDeErro('Erro ao excluir jogo.');
        }
    };

    const textoParaSalvar = () => {
        if (jogoEmEdicao)
            return criandoJogo ? 'Salvando...' : 'Editar jogo';

        return criandoJogo ? 'Criando...' : 'Criar jogo';
    };

    const renderListaJogos = () => {
        if (loadingJogos) return <p>Carregando jogos do dia...</p>;
        if (!jogos.length) return <p className="text-muted">Nenhum jogo criado para hoje</p>;

        return (
            <>
                {jogos.map((jogo, index) => {
                    const estaEditando = editandoResultadoId === jogo.id;
                    const edicao = edicaoResultado[jogo.id] || {gamesA: '', gamesB: ''};

                    return (
                        <div className="card mb-2 shadow-sm" key={jogo.id}>
                            <div className="card-body">
                                <div className="card-title d-flex justify-content-between mb-2">
                                    <strong className="fw-bold d-block">Jogo {index + 1}</strong>
                                    {
                                        !diaFinalizado
                                        &&
                                        <Dropdown
                                            isOpen={dropdownAbertoId === jogo.id}
                                            toggle={() => toggleDropdown(jogo.id)}
                                            className="ms-2"
                                        >
                                            <DropdownToggle color="transparent">
                                                <FaEllipsisV/>
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem
                                                    onClick={() => handleAbrirModalParaEditarJogo(jogo)}
                                                >
                                                    Editar
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => handleDuplicarJogo(jogo.id)}
                                                >
                                                    Duplicar
                                                </DropdownItem>
                                                <DropdownItem divider/>
                                                <DropdownItem
                                                    onClick={() => handleExcluirJogo(jogo.id)}
                                                >
                                                    Excluir
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    }
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="fw-semibold">
                                        {nomeJogador(jogo.idJogadorA1)} / {nomeJogador(jogo.idJogadorA2)}
                                    </div>
                                    <div className="text-end">
                                        {estaEditando ? (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-center"
                                                style={{width: '70px', display: 'inline-block'}}
                                                value={edicao.gamesA}
                                                onChange={e => alterarCampoResultado(jogo.id, 'gamesA', e.target.value)}
                                            />
                                        ) : (
                                            <span className="badge bg-primary fs-6 px-3">
                                            {jogo.gamesA != null ? jogo.gamesA : '-'}
                                        </span>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="fw-semibold">
                                        {nomeJogador(jogo.idJogadorB1)} / {nomeJogador(jogo.idJogadorB2)}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        {estaEditando ? (
                                            <>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm text-center"
                                                    style={{width: '70px'}}
                                                    value={edicao.gamesB}
                                                    onChange={e => alterarCampoResultado(jogo.id, 'gamesB', e.target.value)}
                                                />
                                            </>
                                        ) : (
                                            <>
                                            <span className="badge bg-primary fs-6 px-3">
                                                {jogo.gamesB != null ? jogo.gamesB : '-'}
                                            </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {!estaEditando && !diaFinalizado &&
                                    <button
                                        className="btn btn-success btn-sm w-100 mt-2"
                                        onClick={() => iniciarEdicaoResultado(jogo)}
                                    >
                                        Lançar resultado
                                    </button>
                                }
                                {estaEditando &&
                                    <div className="d-flex gap-2 mt-2">
                                        <button
                                            className="btn btn-success btn-sm w-50"
                                            onClick={() => salvarResultado(jogo.id)}
                                            disabled={salvandoResultadoId === jogo.id}
                                        >
                                            {salvandoResultadoId === jogo.id ? 'Salvando...' : 'Salvar resultado'}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm w-50"
                                            onClick={() => cancelarEdicaoResultado(jogo.id)}
                                            disabled={salvandoResultadoId === jogo.id}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                    );
                })}

                {!diaFinalizado &&
                    <button
                        className="btn btn-warning w-100 my-3"
                        onClick={handleFinalizarJogoDoDia}
                        disabled={finalizandoJogoDoDia}
                    >
                        {finalizandoJogoDoDia ? 'Processando jogos...' : 'Finalizar dia de jogo'}
                    </button>
                }
            </>
        );
    };

    return (
        <div className="container">
            <div className="text-center py-3">
                <h2 className="fw-bold mb-1">
                    {diaFinalizado
                        ? `Jogos do dia ${new Date(diaDeJogo.data).toLocaleDateString('pt-BR')}`
                        : "Gerenciar jogos do dia"}
                </h2>
                <p className="text-muted mb-3">
                    {diaFinalizado
                        ? "Este dia de jogo já foi finalizado."
                        : "Veja os jogos do dia e crie novos jogos rapidamente."}
                </p>

                {!diaFinalizado &&
                    <button
                        className="btn btn-success rounded-circle shadow float-action-button"
                        onClick={toggleModalNovoJogo}
                        disabled={loadingJogadores}
                        title="Novo Jogo"
                    >
                        <FaPlus size={24}/>
                    </button>
                }
            </div>

            {loadingJogadores && <p>Carregando jogadores...</p>}
            {!loadingJogadores && renderListaJogos()}

            <Modal isOpen={modalNovoJogoAberto} toggle={toggleModalNovoJogo}>
                <ModalHeader toggle={toggleModalNovoJogo}>
                    {
                        !jogoEmEdicao ? 'Criar novo jogo' : 'Editar jogo'
                    }
                </ModalHeader>
                <ModalBody>
                    <div className="row g-2">
                        <div className="col-md-6">
                            <label className="form-label">Dupla A - Jogador 1</label>
                            {renderSelectJogador(
                                novoJogo.idJogadorA1,
                                v => handleChangeNovoJogo('idJogadorA1', v),
                                'Selecione o jogador',
                                [novoJogo.idJogadorA2, novoJogo.idJogadorB1, novoJogo.idJogadorB2]
                            )}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Dupla A - Jogador 2</label>
                            {renderSelectJogador(
                                novoJogo.idJogadorA2,
                                v => handleChangeNovoJogo('idJogadorA2', v),
                                'Selecione o jogador',
                                [novoJogo.idJogadorA1, novoJogo.idJogadorB1, novoJogo.idJogadorB2]
                            )}
                        </div>
                        <div className="col-md-6 mt-2">
                            <label className="form-label">Dupla B - Jogador 1</label>
                            {renderSelectJogador(
                                novoJogo.idJogadorB1,
                                v => handleChangeNovoJogo('idJogadorB1', v),
                                'Selecione o jogador',
                                [novoJogo.idJogadorA1, novoJogo.idJogadorA2, novoJogo.idJogadorB2]
                            )}
                        </div>
                        <div className="col-md-6 mt-2">
                            <label className="form-label">Dupla B - Jogador 2</label>
                            {renderSelectJogador(
                                novoJogo.idJogadorB2,
                                v => handleChangeNovoJogo('idJogadorB2', v),
                                'Selecione o jogador',
                                [novoJogo.idJogadorA1, novoJogo.idJogadorA2, novoJogo.idJogadorB1]
                            )}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn btn-secondary" onClick={toggleModalNovoJogo}>
                        Cancelar
                    </button>
                    <button className="btn btn-success" onClick={criarJogo} disabled={criandoJogo}>
                        {textoParaSalvar()}
                    </button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
